import { useCallback, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ApiError, fetchFastApi } from "../../api/fastapi";
import {
  AnalyticsColumnResult,
  AnalyticsColumnResultSchema,
} from "../../models/audit/analyticsColumnResult";
import { OutcomeType } from "../../models/audit/attributeTestColumn";

const overrideEnvelopeSchema = z.object({
  result: AnalyticsColumnResultSchema,
});

export interface AnalyticsOverrideInput {
  outcome: OutcomeType;
  outcomeDescription: string;
  /**
   * The denominator for the conclusion, passed only when the auditor supplies
   * one. The route can resolve a denominator from the verdict or the run
   * manifest and refuses a supplied figure that contradicts it, so one is sent
   * only for a cell that resolves none of its own — never inferred and sent on
   * the auditor's behalf, which would turn an answerable cell into a 422.
   */
  populationTotal?: number;
}

/**
 * The refusal that cannot be retried: the route answers 409 when the row being
 * overridden is no longer the live one — it was superseded already, or a
 * concurrent override took the live slot. Every write against that id is
 * refused the same way, so the caller has to re-read the cell rather than let
 * the auditor try again.
 *
 * Falsy like the other two non-saved outcomes, so it cannot be mistaken for a
 * saved verdict by a caller that only asks whether one came back.
 */
export const OVERRIDE_SUPERSEDED = false;

/**
 * The four outcomes of an override, all distinguishable by the return of
 * `submitOverride`:
 *
 * - the saved verdict — the write committed and its successor row parsed;
 * - `null` — the write committed but the successor could not be read back, so
 *   the cell exists yet this panel could not load it;
 * - `OVERRIDE_SUPERSEDED` — the write did not commit and no retry against this
 *   id can, because the row it targets is no longer the live one;
 * - `undefined` — the write did not commit, and the auditor can answer what the
 *   route said by correcting a field.
 *
 * `if (!override)` bails on all three non-saved outcomes; they are split so a
 * caller that wants to close the dialog and invalidate the shell on the
 * committed-but-unreadable path, or to re-read the cell and disarm its Save on
 * the superseded one, can tell them apart, without the guard changing meaning
 * for a caller that does not.
 */
export type AnalyticsOverrideResult =
  | AnalyticsColumnResult
  | null
  | typeof OVERRIDE_SUPERSEDED
  | undefined;

/**
 * Record an auditor's own answer for an analytics cell.
 *
 * **This is one write behind two verbs, not two writes.**
 * `POST /analytics-results/{id}/human-review` and
 * `PATCH /analytics-results/{id}` share a request model and a handler — the
 * former's body is `return await update_analytics_column_result_endpoint(...)` —
 * so a surface offering both would offer the same write twice. The named verb is
 * the one used, matching the attribute-test panel's `human-review` call.
 *
 * Despite the `PATCH` spelling neither verb is a partial update. Both fields are
 * required together, because a field taken from the live row would sign the
 * judge's words as the auditor's; that is why this cannot back the per-field
 * `InlineEditable*` editing the attribute-test overview offers.
 *
 * The answer supersedes rather than updates, so it comes back under a **new id**
 * and the overridden row stops being writable. The caller must retarget the
 * cell's selection onto the returned verdict, or the panel stays pointed at a
 * row every subsequent write answers 409.
 */
export function useAnalyticsResultOverride() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitOverride = useCallback(
    async (
      resultId: string,
      input: AnalyticsOverrideInput,
    ): Promise<AnalyticsOverrideResult> => {
      setIsSubmitting(true);
      try {
        const response = await fetchFastApi({
          method: "POST",
          path: `/matching/analytics-results/${resultId}/human-review`,
          // The read-back below reports a parse miss as a committed-but-
          // unreadable save, so a refusal must throw rather than resolve: an
          // error envelope that resolved would fail that parse and tell the
          // auditor their conclusion was saved when nothing was written.
          throwOnErrorEnvelope: true,
          body: {
            outcome: input.outcome,
            outcome_description: input.outcomeDescription,
            ...(input.populationTotal === undefined
              ? {}
              : { population_total: input.populationTotal }),
          },
        });
        // Not inside the throw path: the supersession is committed by the time
        // the payload is read, and `AnalyticsColumnResultSchema` enforces
        // invariants the route does not know it must preserve onto a successor
        // row. Reporting a failed save over one of those would send the auditor
        // to write their conclusion again, against a row every write now
        // refuses.
        const parsed = overrideEnvelopeSchema.safeParse(response);
        if (!parsed.success) {
          console.error(
            "Saved analytics verdict did not parse:",
            parsed.error.issues,
          );
          toast.warning(
            "Your conclusion was saved, but this panel could not read it back. Reload the page to see it.",
          );
          // Committed but unreadable: distinct from the `undefined` a failed
          // write returns, so the caller can act on the write having landed.
          return null;
        }
        return parsed.data.result;
      } catch (error) {
        console.error("Error overriding analytics verdict:", error);
        // The status is the only thing separating a refusal the auditor can
        // answer from one that stands however many times they retry, and the
        // route draws that line at 409.
        if (error instanceof ApiError && error.status === 409) {
          // Silent on purpose. The pane answers this refusal itself, with a
          // notice that the conclusion can no longer be saved and a disarmed
          // Save; the route's detail would fight that notice, since it closes
          // by telling the auditor to re-read the cell and try again, and names
          // the verdict ids it compared while doing so.
          return OVERRIDE_SUPERSEDED;
        }
        // The route refuses a contradicted population figure and a cell with no
        // denominator, and each refusal names the field to correct. "Failed to
        // save" would strand an auditor who only needs to drop or supply one.
        toast.error(
          error instanceof ApiError
            ? error.getUserMessage()
            : "Failed to save conclusion",
        );
        // Nothing was written: `undefined` marks this apart from the committed-
        // but-unreadable `null` above.
        return undefined;
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { isSubmitting, submitOverride };
}
